import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  createUserRole,
  getRolePermissions,
  getUserRoles,
  updateRolePermissions,
} from '../services/user_service'
import './Permissions.css'

const GROUP_RULES = [
  ['Customers', ['Customer']],
  ['Books', ['Book', 'Bundle']],
  ['Authors', ['Author']],
  ['Samples', ['Sample']],
  ['Orders', ['Order', 'Invoice', 'PaymentPlan']],
  ['Stores', ['releaseOrder', 'returnOrder', 'releaseSample', 'returnSample']],
  ['Stock', ['Stock']],
  ['Royalties', ['Royalty', 'Royalties']],
  ['Print Requests', ['Print']],
  ['Users', ['User']],
  ['Payments', ['Payment']],
  ['Old Payments', ['OldPayment']],
  ['Reports', ['Report']],
]

const PRIORITY_GROUPS = {
  releaseOrder: 'Stores',
  returnOrder: 'Stores',
  releaseSample: 'Stores',
  returnSample: 'Stores',
  addOldPayment: 'Old Payments',
  editOldPayment: 'Old Payments',
  deleteOldPayment: 'Old Payments',
  viewOldPayment: 'Old Payments',
  editAuthorRoyalty: 'Royalties',
}

const getPermissionGroup = (action) => {
  if (PRIORITY_GROUPS[action]) return PRIORITY_GROUPS[action]
  return (
    GROUP_RULES.find(([, tokens]) =>
      tokens.some((token) => action.includes(token)),
    )?.[0] || 'Other'
  )
}

const getPermissionLabel = (action) =>
  action
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (letter) => letter.toUpperCase())
    .replace('Payment Plan', 'Payment plan')

function NewRoleModal({ onClose, onCreated }) {
  const [roleName, setRoleName] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')

  const submit = async (event) => {
    event.preventDefault()
    setIsSaving(true)
    setError('')
    try {
      const role = await createUserRole(roleName.trim())
      onCreated(role)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div
      className='permissions-modal__backdrop'
      role='presentation'
      onMouseDown={(event) =>
        event.target === event.currentTarget && !isSaving && onClose()
      }
    >
      <section
        className='permissions-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='new-role-title'
      >
        <div className='permissions-modal__header'>
          <div>
            <span className='app-content__eyebrow'>Access control</span>
            <h2 id='new-role-title'>New role</h2>
          </div>
          <button
            type='button'
            onClick={onClose}
            disabled={isSaving}
            aria-label='Close role form'
          >
            x
          </button>
        </div>
        {error && (
          <div className='permissions-page__error' role='alert'>
            {error}
          </div>
        )}
        <form className='permissions-role-form' onSubmit={submit}>
          <label>
            Role name
            <input
              value={roleName}
              onChange={(event) => setRoleName(event.target.value)}
              required
              autoFocus
            />
          </label>
          <div className='permissions-modal__actions'>
            <button type='button' onClick={onClose} disabled={isSaving}>
              Cancel
            </button>
            <button
              className='permissions-button--primary'
              type='submit'
              disabled={isSaving || !roleName.trim()}
            >
              {isSaving ? 'Creating...' : 'Create role'}
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}

function Permissions() {
  const [roles, setRoles] = useState([])
  const [selectedRoleId, setSelectedRoleId] = useState('')
  const [permissions, setPermissions] = useState([])
  const [selectedPermissions, setSelectedPermissions] = useState([])
  const [savedPermissions, setSavedPermissions] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [showNewRole, setShowNewRole] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  useEffect(() => {
    const controller = new AbortController()
    setIsLoading(true)
    getUserRoles(controller.signal)
      .then(setRoles)
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false)
      })
    return () => controller.abort()
  }, [])

  useEffect(() => {
    if (!selectedRoleId) {
      setPermissions([])
      setSelectedPermissions([])
      setSavedPermissions([])
      return
    }
    const controller = new AbortController()
    setIsLoading(true)
    setError('')
    setNotice('')
    getRolePermissions(selectedRoleId, controller.signal)
      .then((result) => {
        const available = Array.isArray(result?.permissions)
          ? result.permissions
          : []
        const assigned = Array.isArray(result?.assigned) ? result.assigned : []
        setPermissions(available)
        setSelectedPermissions(assigned)
        setSavedPermissions(assigned)
      })
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') setError(requestError.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false)
      })
    return () => controller.abort()
  }, [selectedRoleId])

  const groups = useMemo(() => {
    const grouped = new Map()
    permissions.forEach((action) => {
      const group = getPermissionGroup(action)
      grouped.set(group, [...(grouped.get(group) || []), action])
    })
    const order = [...GROUP_RULES.map(([name]) => name), 'Other']
    return order
      .filter((name) => grouped.has(name))
      .map((name) => ({ name, actions: grouped.get(name) }))
  }, [permissions])

  const isDirty =
    [...selectedPermissions].sort().join('|') !==
    [...savedPermissions].sort().join('|')

  const togglePermission = (action) => {
    setNotice('')
    setSelectedPermissions((current) =>
      current.includes(action)
        ? current.filter((permission) => permission !== action)
        : [...current, action],
    )
  }

  const toggleGroup = (actions) => {
    const allSelected = actions.every((action) =>
      selectedPermissions.includes(action),
    )
    setSelectedPermissions((current) =>
      allSelected
        ? current.filter((permission) => !actions.includes(permission))
        : [...new Set([...current, ...actions])],
    )
  }

  const savePermissions = async () => {
    setIsSaving(true)
    setError('')
    setNotice('')
    try {
      const result = await updateRolePermissions(
        selectedRoleId,
        selectedPermissions,
      )
      const assigned = Array.isArray(result?.assigned) ? result.assigned : []
      setSelectedPermissions(assigned)
      setSavedPermissions(assigned)
      setNotice('Permissions updated successfully.')
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSaving(false)
    }
  }

  const roleCreated = (role) => {
    setRoles((current) =>
      [...current, role].sort((left, right) =>
        left.role_name.localeCompare(right.role_name),
      ),
    )
    setShowNewRole(false)
    setSelectedRoleId(String(role.roleid))
    setNotice('Role created. Select its permissions below.')
  }

  return (
    <div className='permissions-page'>
      <nav className='permissions-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <Link to='/admin/users'>Users</Link>
        <span aria-hidden='true'>/</span>
        <strong>Permissions</strong>
      </nav>
      <header className='permissions-page__header'>
        <div>
          <span className='app-content__eyebrow'>Access control</span>
          <h1>Roles and permissions</h1>
          <p>
            Choose what each system role can view, create, edit, and approve.
          </p>
        </div>
        <button
          className='permissions-button permissions-button--primary'
          type='button'
          onClick={() => setShowNewRole(true)}
        >
          + New role
        </button>
      </header>
      {error && (
        <div className='permissions-page__error' role='alert'>
          {error}
        </div>
      )}
      {notice && (
        <div className='permissions-page__notice' role='status'>
          {notice}
        </div>
      )}
      <section
        className='permissions-workspace'
        aria-labelledby='permissions-workspace-title'
      >
        <div className='permissions-toolbar'>
          <div>
            <span className='app-content__eyebrow'>Role</span>
            <h2 id='permissions-workspace-title'>Permission matrix</h2>
          </div>
          <label>
            <span className='sr-only'>Select role</span>
            <select
              value={selectedRoleId}
              onChange={(event) => setSelectedRoleId(event.target.value)}
              disabled={isLoading && !roles.length}
            >
              <option value=''>Select a role</option>
              {roles.map((role) => (
                <option key={role.roleid} value={role.roleid}>
                  {role.role_name}
                </option>
              ))}
            </select>
          </label>
          <button
            className='permissions-button permissions-button--primary'
            type='button'
            onClick={savePermissions}
            disabled={!selectedRoleId || !isDirty || isLoading || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save permissions'}
          </button>
        </div>
        {!selectedRoleId ? (
          <div className='permissions-empty'>
            Select a role to view and modify its permissions.
          </div>
        ) : isLoading ? (
          <div className='permissions-empty'>Loading permissions...</div>
        ) : (
          <div className='permissions-grid'>
            {groups.map((group) => {
              const allSelected = group.actions.every((action) =>
                selectedPermissions.includes(action),
              )
              return (
                <fieldset className='permission-group' key={group.name}>
                  <legend>{group.name}</legend>
                  <button
                    className='permission-group__toggle'
                    type='button'
                    onClick={() => toggleGroup(group.actions)}
                  >
                    {allSelected ? 'Clear all' : 'Select all'}
                  </button>
                  <div className='permission-group__options'>
                    {group.actions.map((action) => (
                      <label key={action}>
                        <input
                          type='checkbox'
                          checked={selectedPermissions.includes(action)}
                          onChange={() => togglePermission(action)}
                        />
                        <span>{getPermissionLabel(action)}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )
            })}
          </div>
        )}
      </section>
      {showNewRole && (
        <NewRoleModal
          onClose={() => setShowNewRole(false)}
          onCreated={roleCreated}
        />
      )}
    </div>
  )
}

export default Permissions
